import { motion } from 'framer-motion';
import { 
  CommandLineIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  CubeTransparentIcon,
  LightBulbIcon,
  BuildingOfficeIcon,
  CodeBracketIcon,
  CloudIcon,
  UserGroupIcon,
  BookOpenIcon,
  WrenchScrewdriverIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import ContactForm from '../components/contact/ContactForm';

const AgenticAI = () => {
  const transformPoints = [
    {
      title: "Plan",
      description: "Strategic decision-making and goal setting",
      icon: LightBulbIcon,
      code: "PLN_001"
    },
    {
      title: "Reason",
      description: "Logical analysis and problem-solving",
      icon: CpuChipIcon,
      code: "RSN_002"
    },
    {
      title: "Learn",
      description: "Continuous improvement and adaptation",
      icon: BookOpenIcon,
      code: "LRN_003"
    },
    {
      title: "Act",
      description: "Autonomous execution and implementation",
      icon: CommandLineIcon,
      code: "ACT_004"
    }
  ];

  const whoWeHelp = [
    {
      title: "Enterprises looking to scale automation",
      icon: BuildingOfficeIcon,
      code: "ENT_001"
    },
    {
      title: "SaaS companies integrating AI copilots",
      icon: CodeBracketIcon,
      code: "SAS_002"
    },
    {
      title: "Operations teams replacing legacy RPA",
      icon: WrenchScrewdriverIcon,
      code: "OPS_003"
    },
    {
      title: "Compliance-heavy sectors (Finance, Healthcare, Legal)",
      icon: ShieldCheckIcon,
      code: "CMP_004"
    }
  ];

  const services = [
    {
      title: "Agent Design & Development",
      description: "We build autonomous agents tailored to your goals, using the latest open and proprietary frameworks.",
      features: [
        "Multi-Agent System (MAS) Architecture",
        "Agent Memory, Goals, and Autonomy",
        "Custom Workflows with LangChain, CrewAI, AutoGen",
        "LLM Fine-Tuning & Prompt Engineering"
      ],
      example: "Deploy a planning agent that handles end-to-end client onboarding with minimal human input.",
      icon: CpuChipIcon,
      code: "SRV_001"
    },
    {
      title: "Integration Into Your Tech Stack",
      description: "Your agents won't sit in a sandbox. We integrate them across your existing tools and platforms.",
      features: [
        "API Integrations with CRMs, ERPs, SaaS Tools",
        "Combine RPA + Agents for complex workflows",
        "Connect to Zapier, n8n, Make, Camunda"
      ],
      example: "Sales agent pulling data from Salesforce, updating tasks in Asana, and triggering billing in Stripe — autonomously.",
      icon: CubeTransparentIcon,
      code: "SRV_002"
    },
    {
      title: "Secure & Scalable Infrastructure",
      description: "We set up everything your agents need to think, recall, and act safely and at scale.",
      features: [
        "Vector Databases (Pinecone, Weaviate, Redis)",
        "On-Prem & Cloud LLM Deployment (Azure, Vertex AI)",
        "Observability & Logging (Langfuse, Arize AI)",
        "Persistent Memory & State Management"
      ],
      example: "",
      icon: CloudIcon,
      code: "SRV_003"
    },
    {
      title: "Privacy, Security & Compliance by Design",
      description: "We build AI that respects data and privacy from day one.",
      features: [
        "Data Anonymization & Redaction (Private AI, Presidio)",
        "Private LLM Hosting for Sensitive Data",
        "Compliance with GDPR, HIPAA, SOC 2, ISO 27001",
        "Agent Behavior Auditing & Explainability"
      ],
      example: "Don't risk shadow AI. Make privacy a core feature.",
      icon: ShieldCheckIcon,
      code: "SRV_004"
    },
    {
      title: "Human-Centered Interfaces",
      description: "Your agents can live where your teams and users already are.",
      features: [
        "Conversational Interfaces: Slack, MS Teams, Webchat",
        "Voice Agents via Twilio & WebRTC",
        "Embedded UIs using React & WebSocket APIs"
      ],
      example: "",
      icon: ChatBubbleLeftRightIcon,
      code: "SRV_005"
    }
  ];

  const enablementFeatures = [
    {
      title: "Agent Interaction Training for Staff",
      icon: AcademicCapIcon,
      code: "TRN_001"
    },
    {
      title: "Ethical AI & Responsible Usage Workshops",
      icon: UserGroupIcon,
      code: "ETH_002"
    },
    {
      title: "Living Docs, SOPs, and Prompt Libraries",
      icon: DocumentTextIcon,
      code: "DOC_003"
    }
  ];

  const techStack = [
    {
      category: "Frameworks",
      items: "LangChain, CrewAI, AutoGen",
      code: "FRM_001"
    },
    {
      category: "LLMs",
      items: "OpenAI GPT-4, Claude, LLaMA, Mixtral",
      code: "LLM_002"
    },
    {
      category: "Tools",
      items: "Pinecone, Hugging Face, Langfuse, Truera, Twilio",
      code: "TLS_003"
    },
    {
      category: "Platforms",
      items: "Azure, AWS, GCP, On-Prem LLMs",
      code: "PLT_004"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <header className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:14px_24px]" />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          {/* Header decoration */}
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [SYSTEM::AGENTIC_AI_ACCESS]
          </div>

          <div className="mb-8">
            <CpuChipIcon className="w-16 h-16 text-indigo-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Empower Your Business with Agentic AI
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto font-mono">
              Build Intelligent, Autonomous Agents That Think, Act, and Deliver
            </p>
          </div>
        </motion.div>
      </header>

      {/* Transform Workflows Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [AI::TRANSFORMATION]
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Transform Workflows with Next-Generation AI Agents
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-4xl mx-auto font-mono mb-12">
              Hello to autonomous, decision-making agents that integrate deeply into your business processes. Whether you're automating operations, enhancing customer experience, or enabling data-driven decisions — Agentic AI is the future of intelligent business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {transformPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                  group-hover:border-indigo-500/40 transition-all duration-300" />
                
                <div className="relative p-8">
                  <div className="flex items-center justify-between mb-6">
                    <point.icon className="w-8 h-8 text-indigo-400" />
                    <div className="font-mono text-xs text-indigo-500/60">{point.code}</div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3 font-mono">{point.title}</h3>
                  <p className="text-slate-400 font-mono">{point.description}</p>

                  {/* Corner decorations */}
                  <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative group inline-flex items-center gap-2"
            >
              <div className="absolute inset-0 bg-indigo-500/20 rounded backdrop-blur-sm group-hover:bg-indigo-500/30 transition-colors" />
              <div className="relative px-8 py-3 text-indigo-400 border border-indigo-500/50 rounded 
                group-hover:border-indigo-400 transition-colors font-mono"
              >
                <span>Ready to build your first agent? Book a Consultation →</span>
              </div>
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* Who We Help Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [AI::TARGET_CLIENTS]
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Who We Help
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whoWeHelp.map((client, index) => (
              <motion.div
                key={client.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                  group-hover:border-indigo-500/40 transition-all duration-300" />
                
                <div className="relative p-8">
                  <div className="flex items-center justify-between mb-6">
                    <client.icon className="w-8 h-8 text-indigo-400" />
                    <div className="font-mono text-xs text-indigo-500/60">{client.code}</div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3 font-mono">{client.title}</h3>

                  {/* Corner decorations */}
                  <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [AI::SERVICES]
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Our Agentic AI Services — Built for the Real World
              </span>
            </h2>
          </div>

          <div className="space-y-12">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.01 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                  group-hover:border-indigo-500/40 transition-all duration-300" />
                
                <div className="relative p-8">
                  <div className="flex items-center justify-between mb-6">
                    <service.icon className="w-8 h-8 text-indigo-400" />
                    <div className="font-mono text-xs text-indigo-500/60">{service.code}</div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 font-mono">{service.title}</h3>
                  <p className="text-slate-400 font-mono mb-6">{service.description}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-bold text-white mb-3 font-mono">Features:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="text-slate-400 font-mono text-sm flex items-start">
                            <span className="text-indigo-400 mr-2">•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {service.example && (
                      <div>
                        <h4 className="text-lg font-bold text-white mb-3 font-mono">Example:</h4>
                        <p className="text-slate-400 font-mono text-sm italic bg-slate-800/30 p-4 rounded border-l-2 border-indigo-500/30">
                          {service.example}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Corner decorations */}
                  <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Enablement & Training Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [AI::ENABLEMENT]
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Enablement & Training
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto font-mono">
              We don't just build—we empower your teams to thrive with AI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {enablementFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                  group-hover:border-indigo-500/40 transition-all duration-300" />
                
                <div className="relative p-8 text-center">
                  <div className="flex items-center justify-between mb-6">
                    <feature.icon className="w-8 h-8 text-indigo-400 mx-auto" />
                    <div className="font-mono text-xs text-indigo-500/60">{feature.code}</div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-white mb-3 font-mono">{feature.title}</h3>

                  {/* Corner decorations */}
                  <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Tech Stack Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [AI::TECH_STACK]
          </div>

          <div className="text-center mb-16">
            <BeakerIcon className="w-12 h-12 text-indigo-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Our Tech Stack
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {techStack.map((stack, index) => (
              <motion.div
                key={stack.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                  group-hover:border-indigo-500/40 transition-all duration-300" />
                
                <div className="relative p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="font-mono text-xs text-indigo-500/60">{stack.code}</div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3 font-mono">{stack.category}</h3>
                  <p className="text-slate-400 font-mono text-sm">{stack.items}</p>

                  {/* Corner decorations */}
                  <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [AI::FAQ]
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Frequently Asked Questions
              </span>
            </h2>
          </div>

          <div className="space-y-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                group-hover:border-indigo-500/40 transition-all duration-300" />
              
              <div className="relative p-8">
                <h3 className="text-xl font-bold text-white mb-4 font-mono">
                  Q: Can agents work with our existing tools?
                </h3>
                <p className="text-slate-400 font-mono">
                  Absolutely. We design agents to integrate via APIs or RPA connectors with your tech stack.
                </p>

                {/* Corner decorations */}
                <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                group-hover:border-indigo-500/40 transition-all duration-300" />
              
              <div className="relative p-8">
                <h3 className="text-xl font-bold text-white mb-4 font-mono">
                  Q: What about data privacy and PII?
                </h3>
                <p className="text-slate-400 font-mono">
                  We implement PII redaction, private LLM hosting, and governance controls by default.
                </p>

                {/* Corner decorations */}
                <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                group-hover:border-indigo-500/40 transition-all duration-300" />
              
              <div className="relative p-8">
                <h3 className="text-xl font-bold text-white mb-4 font-mono">
                  Q: Is this different from traditional automation or chatbots?
                </h3>
                <p className="text-slate-400 font-mono">
                  Yes — Agentic AI enables systems that reason, not just respond. Agents are adaptive and goal-driven, not scripted.
                </p>

                {/* Corner decorations */}
                <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
              </div>
            </motion.div>
          </div>

          {/* Footer decoration */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [END::FAQ]
          </div>
        </motion.div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [SYSTEM::CONTACT_FORM]
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Initialize Connection
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto font-mono">
              Want a personalized Agentic AI roadmap for your business?<br />
              Book a free strategy session and see what's possible.
            </p>
          </div>

          <ContactForm />

          {/* Footer decoration */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [END::CONTACT_FORM]
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AgenticAI;